// frontend/src/pages/Home.js  (v3.0 – modernized, SEO + conversion optimized)
import '../custom.css'

import React, { useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { CheckCircleFill, StarFill } from 'react-bootstrap-icons'
import axios from 'axios'
import { motion } from 'framer-motion'
import TextLoop from 'react-text-loop'

import HomeAIPreview from '../components/HomeAIPreview'

/* ─── assets ─── */
const appMockup1      = '/assets/images/greatlysocialapp.png'
const appStoreBadge   = 'https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg'
const googlePlayBadge = 'https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg'
const testimonyImg    = 'https://images.pexels.com/photos/3769021/pexels-photo-3769021.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=160&w=160'
const stepPlan        = 'https://images.pexels.com/photos/6476588/pexels-photo-6476588.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=300&w=500'
const stepCreate      = 'https://images.pexels.com/photos/4348404/pexels-photo-4348404.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=300&w=500'
const stepPublish     = 'https://images.pexels.com/photos/5081925/pexels-photo-5081925.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=300&w=500'

const placeholderImg     = 'https://images.pexels.com/photos/2983464/pexels-photo-2983464.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=450&w=450'
const placeholderCaption = "✨ Ready to post? Describe an idea on the left and let Greatly Social's AI create a photo & caption for you!"

/* ─── animation variants ─── */
const fadeUp = {
  hidden : { opacity: 0, y: 36 },
  show   : { opacity: 1, y: 0, transition: { duration: 0.55 } },
}
const stagger = {
  hidden: {},
  show  : { transition: { staggerChildren: 0.12 } },
}

/* ─── platform list ─── */
const PLATFORMS = [
  { name: 'Facebook',    url: 'https://img.icons8.com/color/96/facebook-new.png' },
  { name: 'Instagram',   url: 'https://img.icons8.com/color/96/instagram-new.png' },
  { name: 'X / Twitter', url: 'https://img.icons8.com/color/96/twitterx--v1.png' },
  { name: 'LinkedIn',    url: 'https://img.icons8.com/color/96/linkedin.png' },
  { name: 'TikTok',      url: 'https://img.icons8.com/color/96/tiktok--v1.png' },
  { name: 'Pinterest',   url: 'https://img.icons8.com/color/96/pinterest--v1.png' },
  { name: 'YouTube',     url: 'https://img.icons8.com/color/96/youtube-play.png' },
]

/* ─── features ─── */
const FEATURES = [
  {
    icon : 'fas fa-link',
    color: '#6366f1',
    bg   : '#eef2ff',
    badge: 'New',
    title: 'Link in Bio',
    desc : 'A beautiful, customizable bio page for all your links — with click analytics and your latest posts, auto-updated.',
  },
  {
    icon : 'fas fa-address-book',
    color: '#0d9488',
    bg   : '#f0fdfa',
    badge: 'New',
    title: 'Built-in CRM',
    desc : 'Capture leads from your bio page and manage every contact in one place — tag, filter, import and export with a click.',
  },
  {
    icon : 'fas fa-envelope',
    color: '#db2777',
    bg   : '#fce7f3',
    badge: 'New',
    title: 'Email Marketing',
    desc : 'Turn subscribers into customers — send email campaigns straight to your CRM contacts, with one-click unsubscribe built in. SMS coming soon.',
  },
  {
    icon : 'fas fa-magic',
    color: '#7c3aed',
    bg   : '#f5f3ff',
    title: 'AI Post Generator',
    desc : 'Turn a simple idea into a ready-to-publish post — image, caption and hashtags in seconds.',
  },
  {
    icon : 'fas fa-calendar-alt',
    color: '#2563eb',
    bg   : '#eff6ff',
    title: 'Drag-and-Drop Calendar',
    desc : 'Plan a full month of content visually. Drag, drop and reschedule posts in seconds.',
  },
  {
    icon : 'fas fa-video',
    color: '#0891b2',
    bg   : '#ecfeff',
    title: 'Built-in Video Editor',
    desc : 'Trim, resize and add captions right inside Greatly Social — no third-party apps.',
  },
  {
    icon : 'fas fa-clock',
    color: '#16a34a',
    bg   : '#dcfce7',
    title: 'Best Time to Post AI',
    desc : 'Our AI analyzes your audience and suggests the optimal time to maximize reach.',
  },
  {
    icon : 'fas fa-file-alt',
    color: '#d97706',
    bg   : '#fef3c7',
    title: 'File & Media Manager',
    desc : 'Store all your photos and videos in one place, organized and ready to use.',
  },
  {
    icon : 'fas fa-chart-line',
    color: '#dc2626',
    bg   : '#fef2f2',
    title: 'Analytics & Insights',
    desc : 'See what content is working and double down — clear reports, no fluff.',
  },
]

/* ─── steps ─── */
const STEPS = [
  { img: stepPlan,    badge: '01', title: 'Plan your calendar',      desc: 'Drag posts onto dates or bulk-generate a month of ideas with AI.' },
  { img: stepCreate,  badge: '02', title: 'Craft engaging content',  desc: 'Import media, write captions, preview every network — all in one screen.' },
  { img: stepPublish, badge: '03', title: 'Auto-publish everywhere', desc: 'Greatly Social posts on your behalf at exactly the right time.' },
]

/* ─── stat counters ─── */
const STATS = [
  { value: '10,000+', label: 'Businesses using Greatly Social' },
  { value: '2M+',     label: 'Posts published' },
  { value: '70%',     label: 'Average time saved per week' },
  { value: '7',       label: 'Platforms supported' },
]

/* ─── shared inline styles ─── */
const btnPrimary = {
  display       : 'inline-flex',
  alignItems    : 'center',
  gap           : 8,
  padding       : '13px 28px',
  borderRadius  : 10,
  border        : 'none',
  background    : '#2563eb',
  color         : '#ffffff',
  fontSize      : 15,
  fontWeight    : 700,
  cursor        : 'pointer',
  textDecoration: 'none',
  transition    : 'background 0.15s, transform 0.15s',
  boxShadow     : '0 4px 14px rgba(37,99,235,0.35)',
}
const btnOutline = {
  display       : 'inline-flex',
  alignItems    : 'center',
  gap           : 8,
  padding       : '13px 28px',
  borderRadius  : 10,
  border        : '2px solid #2563eb',
  background    : 'transparent',
  color         : '#2563eb',
  fontSize      : 15,
  fontWeight    : 700,
  cursor        : 'pointer',
  textDecoration: 'none',
  transition    : 'all 0.15s',
}

/* ================================================================
   Component
================================================================ */
export default function Home () {
  /* AI demo state */
  const [idea,            setIdea]       = useState('')
  const [caption,         setCaption]    = useState('')
  const [imgUrl,          setImgUrl]     = useState('')
  const [loading,         setLoading]    = useState(false)
  const [error,           setError]      = useState('')
  const [outOfCredits,    setOOC]        = useState(false)
  const [demoLimitReached,setDemoLimit]  = useState(false)
  const [generated,       setGenerated]  = useState(false)

  const callRoute = (path, prompt) =>
    axios.post(path, { prompt }, { withCredentials: true }).then(r => r.data)

  const generate = async () => {
    if (!idea.trim() || demoLimitReached) return
    const prompt = idea.trim()
    try {
      setLoading(true); setError(''); setOOC(false); setImgUrl(''); setCaption('')
      let data
      try {
        data = await callRoute('/api/ai/generate-post', prompt)
      } catch (err) {
        const st = err.response?.status
        if (st === 401 || st === 403) data = await callRoute('/api/public/ai/demo-post', prompt)
        else if (st === 402) { setOOC(true); return }
        else throw err
      }
      setImgUrl(data.images?.[0] || '')
      setCaption(data.caption || '')
      setGenerated(true)
    } catch (err) {
      if (err.response?.status === 429) setDemoLimit(true)
      else setError(err.response?.data?.error || 'Generation failed')
    } finally {
      setLoading(false)
    }
  }

  const previewImg = generated ? (imgUrl || placeholderImg) : placeholderImg
  const previewCap = generated ? caption : placeholderCaption

  return (
    <>
      {/* ─────────────────────────────────────────
          SEO META (react-helmet)
      ───────────────────────────────────────── */}
      {typeof window !== 'undefined' && (() => {
        document.title = 'Greatly Social — Social Media Scheduling & AI Post Creator'
        const desc = 'Schedule posts to Facebook, Instagram, TikTok, LinkedIn, Pinterest, YouTube and X from one dashboard. AI-powered captions, images and the best time to post — free trial, no credit card.'
        let meta = document.querySelector('meta[name="description"]')
        if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta) }
        meta.content = desc

        // OG tags
        const og = { 'og:title':'Greatly Social — Schedule & Grow on Every Platform', 'og:description':desc, 'og:type':'website', 'og:url':'https://greatlysocial.com', 'twitter:card':'summary_large_image' }
        Object.entries(og).forEach(([p, c]) => {
          let tag = document.querySelector(`meta[property="${p}"]`) || document.querySelector(`meta[name="${p}"]`)
          if (!tag) { tag = document.createElement('meta'); tag.setAttribute(p.startsWith('twitter') ? 'name' : 'property', p); document.head.appendChild(tag) }
          tag.content = c
        })
        return null
      })()}

      <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", color: '#111827' }}>

        {/* ═══════════════════════════════════════
            HERO
        ═══════════════════════════════════════ */}
        <section style={{
          background   : 'linear-gradient(160deg, #0f172a 0%, #1e3a5f 60%, #1d4ed8 100%)',
          padding      : '80px 0 90px',
          position     : 'relative',
          overflow     : 'hidden',
        }}>
          {/* decorative blob */}
          <div style={{ position:'absolute', top:-120, right:-120, width:500, height:500, borderRadius:'50%', background:'rgba(37,99,235,0.2)', filter:'blur(80px)', pointerEvents:'none' }} />
          <div style={{ position:'absolute', bottom:-80, left:-80, width:400, height:400, borderRadius:'50%', background:'rgba(124,58,237,0.15)', filter:'blur(60px)', pointerEvents:'none' }} />

          <div style={{ maxWidth:1140, margin:'0 auto', padding:'0 24px' }}>
            <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:48 }}>

              {/* Left */}
              <motion.div style={{ flex:'1 1 480px' }} variants={fadeUp} initial='hidden' whileInView='show' viewport={{ once:true }}>
                {/* Social proof pill */}
                <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:99, padding:'5px 14px', marginBottom:24 }}>
                  {[...Array(5)].map((_, i) => <StarFill key={i} size={11} color='#fbbf24' />)}
                  <span style={{ fontSize:13, color:'rgba(255,255,255,0.85)', fontWeight:600, marginLeft:4 }}>Trusted by 10,000+ businesses</span>
                </div>

                <h1 style={{ fontSize:'clamp(2rem, 5vw, 3.2rem)', fontWeight:900, color:'#ffffff', lineHeight:1.1, marginBottom:12 }}>
                  {/* The static half carries the target term. `social media scheduler` is
                      60,500/mo at KD 33 (measured 2026-08-15); the previous H1 named no
                      keyword at all, and the title was aimed at `ai social media scheduler`
                      — 30/mo at KD 79. All four rotations still read correctly. */}
                  {/* Explicit trailing space: the spans are display:block so it changes
                      nothing visually, but without it the extracted text reads
                      "...schedulerthatgrows your brand". */}
                  <span style={{ display:'block' }}>{'The social media scheduler that '}</span>
                  <span style={{ color:'#60a5fa' }}>
                    <TextLoop interval={2800} mask springConfig={{ stiffness:180, damping:16 }}>
                      <span>runs itself.</span>
                      <span>saves hours.</span>
                      <span>grows your brand.</span>
                      <span>posts for you.</span>
                    </TextLoop>
                  </span>
                </h1>

                {/* Says plainly what the product IS — nothing on the page did before, and
                    pages holding a category term almost always do. Carries the honest
                    secondary terms alongside the head: `social media planner` (1,600/mo,
                    KD 26) and `social media content calendar` (2,400/mo, KD 40), both of
                    which describe real features. Deliberately does NOT claim "free": there
                    is no free plan, only a 14-day trial, and the `free social media
                    scheduler` cluster — though 3,000/mo at KD 15-20 — would send people to
                    a $9/month product expecting a free tier. */}
                <p style={{ fontSize:18, color:'rgba(255,255,255,0.75)', lineHeight:1.7, margin:'20px 0 32px', maxWidth:520 }}>
                  Greatly Social is a social media scheduler and content calendar for Instagram,
                  TikTok, Facebook, LinkedIn, YouTube, Pinterest, Threads and X. Write a post once,
                  tailor it per network, and schedule everything from one planner — with AI to draft
                  the captions, a link-in-bio page and a built-in CRM.
                </p>

                <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:28 }}>
                  <a href='/register' style={{ ...btnPrimary, fontSize:16, padding:'14px 32px' }}
                    onMouseEnter={e => { e.currentTarget.style.background='#1d4ed8'; e.currentTarget.style.transform='translateY(-1px)' }}
                    onMouseLeave={e => { e.currentTarget.style.background='#2563eb'; e.currentTarget.style.transform='translateY(0)' }}>
                    Start using Greatly Social free
                  </a>
                  <a href='/login' style={{ ...btnOutline, border:'2px solid rgba(255,255,255,0.4)', color:'#fff', fontSize:16 }}
                    onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.1)' }}
                    onMouseLeave={e => { e.currentTarget.style.background='transparent' }}>
                    Log In
                  </a>
                </div>

                <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
                  {['No credit card required', 'Cancel anytime', '14-day free trial'].map(t => (
                    <span key={t} style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'rgba(255,255,255,0.65)' }}>
                      <CheckCircleFill size={13} color='#34d399' />{t}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Right — platform icons grid */}
              <motion.div
                style={{ flex:'1 1 300px', display:'flex', flexDirection:'column', gap:16 }}
                initial={{ opacity:0, x:40 }} whileInView={{ opacity:1, x:0 }}
                transition={{ duration:0.7, delay:0.2 }} viewport={{ once:true }}
              >
                <div style={{ background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:16, padding:'24px', backdropFilter:'blur(8px)' }}>
                  <div style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'rgba(255,255,255,0.45)', marginBottom:16 }}>
                    Post to all your platforms
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:14 }}>
                    {PLATFORMS.map(({ name, url }) => (
                      <div key={name} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:5 }}>
                        <img src={url} alt={name} style={{ width:38, height:38 }} />
                        <span style={{ fontSize:10, color:'rgba(255,255,255,0.5)', fontWeight:500 }}>{name.split(' ')[0]}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Mini stat card */}
                <div style={{ display:'flex', gap:12 }}>
                  {[{ n:'2M+', l:'Posts published' }, { n:'70%', l:'Time saved' }].map(s => (
                    <div key={s.n} style={{ flex:1, background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:12, padding:'16px', textAlign:'center' }}>
                      <div style={{ fontSize:26, fontWeight:900, color:'#60a5fa' }}>{s.n}</div>
                      <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', fontWeight:500, marginTop:2 }}>{s.l}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            SOCIAL PROOF STATS BAR
        ═══════════════════════════════════════ */}
        <section style={{ background:'#f8fafc', borderBottom:'1px solid #e5e7eb', padding:'28px 0' }}>
          <div style={{ maxWidth:1140, margin:'0 auto', padding:'0 24px' }}>
            <motion.div
              style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:0 }}
              variants={stagger} initial='hidden' whileInView='show' viewport={{ once:true }}
            >
              {STATS.map((s, i) => (
                <motion.div key={s.label} variants={fadeUp} style={{
                  flex        : '1 1 180px',
                  textAlign   : 'center',
                  padding     : '12px 24px',
                  borderRight : i < STATS.length - 1 ? '1px solid #e5e7eb' : 'none',
                }}>
                  <div style={{ fontSize:28, fontWeight:900, color:'#2563eb' }}>{s.value}</div>
                  <div style={{ fontSize:13, color:'#6b7280', marginTop:2 }}>{s.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            AI SNEAK-PEEK (moved up — conversion)
        ═══════════════════════════════════════ */}
        <section style={{ background:'#ffffff', padding:'80px 0' }}>
          <div style={{ maxWidth:1140, margin:'0 auto', padding:'0 24px' }}>
            <motion.div style={{ textAlign:'center', marginBottom:52 }} variants={fadeUp} initial='hidden' whileInView='show' viewport={{ once:true }}>
              <div style={{ display:'inline-flex', alignItems:'center', gap:7, background:'#f5f3ff', border:'1px solid #e9d5ff', borderRadius:99, padding:'5px 14px', marginBottom:14 }}>
                <i className='fas fa-magic' style={{ fontSize:12, color:'#7c3aed' }} />
                <span style={{ fontSize:12, fontWeight:700, color:'#7c3aed', textTransform:'uppercase', letterSpacing:'0.07em' }}>Try it free — no account needed</span>
              </div>
              <h2 style={{ fontSize:'clamp(1.6rem, 4vw, 2.4rem)', fontWeight:900, color:'#111827', marginBottom:12 }}>
                ✨ Watch AI build your post in seconds
              </h2>
              <p style={{ fontSize:17, color:'#6b7280', maxWidth:560, margin:'0 auto' }}>
                Type any idea below and our AI will generate a photo and caption ready to post — right here, no signup required.
              </p>
            </motion.div>

            <div style={{ display:'flex', flexWrap:'wrap', gap:32, alignItems:'flex-start' }}>
              {/* Input panel */}
              <motion.div style={{ flex:'1 1 340px' }} variants={fadeUp} initial='hidden' whileInView='show' viewport={{ once:true }}>
                <div style={{ background:'#ffffff', border:'1px solid #e5e7eb', borderRadius:16, padding:28, boxShadow:'0 4px 24px rgba(0,0,0,0.07)' }}>
                  <label style={{ fontSize:14, fontWeight:700, color:'#374151', display:'block', marginBottom:10 }}>
                    Describe your post
                  </label>
                  <textarea
                    rows={3}
                    placeholder='e.g. "Fall home listing in Hartford CT — cozy vibes"'
                    value={idea}
                    onChange={e => setIdea(e.target.value)}
                    disabled={loading || demoLimitReached}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); generate() }}}
                    style={{
                      width:'100%', padding:'10px 12px', borderRadius:9,
                      border:'1.5px solid #e5e7eb', fontSize:14, color:'#111827',
                      resize:'none', outline:'none', boxSizing:'border-box',
                      background: (loading || demoLimitReached) ? '#f9fafb' : '#ffffff',
                      lineHeight:1.5,
                    }}
                    onFocus={e => e.target.style.borderColor='#2563eb'}
                    onBlur={e => e.target.style.borderColor='#e5e7eb'}
                  />

                  <button
                    onClick={generate}
                    disabled={loading || demoLimitReached || !idea.trim()}
                    style={{
                      ...btnPrimary,
                      width  : '100%',
                      marginTop: 12,
                      justifyContent: 'center',
                      fontSize: 15,
                      opacity: (loading || demoLimitReached || !idea.trim()) ? 0.65 : 1,
                      cursor : (loading || demoLimitReached || !idea.trim()) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {loading
                      ? <><Spinner animation='border' size='sm' style={{ width:14, height:14 }} /> Generating…</>
                      : <><i className='fas fa-magic' style={{ fontSize:13 }} /> Generate My Post</>
                    }
                  </button>

                  {/* Alerts */}
                  {outOfCredits && (
                    <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:9, padding:'12px 16px', marginTop:14, textAlign:'center' }}>
                      <div style={{ fontSize:13, fontWeight:600, color:'#dc2626', marginBottom:8 }}>You're out of AI credits.</div>
                      <a href='/login' style={{ ...btnPrimary, fontSize:13, padding:'8px 18px', justifyContent:'center', display:'inline-flex' }}>Log in / upgrade</a>
                    </div>
                  )}
                  {demoLimitReached && (
                    <div style={{ background:'#eff6ff', border:'1px solid #dbeafe', borderRadius:9, padding:'12px 16px', marginTop:14, textAlign:'center' }}>
                      <div style={{ fontSize:13, fontWeight:600, color:'#1d4ed8', marginBottom:8 }}>You've reached the free preview limit!</div>
                      <a href='/register' style={{ ...btnPrimary, fontSize:13, padding:'8px 18px', justifyContent:'center', display:'inline-flex' }}>Create a free account for more</a>
                    </div>
                  )}
                  {error && (
                    <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:9, padding:'12px 16px', marginTop:14, fontSize:13, color:'#dc2626' }}>{error}</div>
                  )}
                  {generated && !error && !outOfCredits && !demoLimitReached && (
                    <div style={{ background:'#dcfce7', border:'1px solid #bbf7d0', borderRadius:9, padding:'10px 16px', marginTop:14, textAlign:'center', fontSize:13, fontWeight:600, color:'#16a34a' }}>
                      🎉 Looks great! Ready to post.
                    </div>
                  )}

                  {/* Prompt ideas */}
                  <div style={{ marginTop:18 }}>
                    <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'#9ca3af', marginBottom:8 }}>Try these ideas</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                      {['New home listing 🏡', 'Summer sale announcement', 'Team spotlight post', 'Before & after transformation'].map(s => (
                        <button key={s} type='button' onClick={() => setIdea(s)} style={{ fontSize:12, padding:'4px 10px', borderRadius:99, border:'1px solid #e5e7eb', background:'#f8fafc', color:'#374151', cursor:'pointer', fontWeight:500 }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Preview panel */}
              <motion.div style={{ flex:'1 1 320px' }} initial={{ opacity:0, y:24 }} whileInView={{ opacity:1, y:0 }} transition={{ duration:0.6, delay:0.15 }} viewport={{ once:true }}>
                <HomeAIPreview caption={previewCap} imgUrl={previewImg} />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            FEATURES GRID
        ═══════════════════════════════════════ */}
        <section style={{ background:'#f8fafc', padding:'80px 0' }}>
          <div style={{ maxWidth:1140, margin:'0 auto', padding:'0 24px' }}>
            <motion.div style={{ textAlign:'center', marginBottom:52 }} variants={fadeUp} initial='hidden' whileInView='show' viewport={{ once:true }}>
              <h2 style={{ fontSize:'clamp(1.6rem, 4vw, 2.3rem)', fontWeight:900, color:'#111827', marginBottom:12 }}>
                Everything you need to grow — in one platform
              </h2>
              <p style={{ fontSize:17, color:'#6b7280', maxWidth:580, margin:'0 auto' }}>
                Create and schedule with AI, grow your audience with a link-in-bio, capture leads into a built-in CRM — and soon, reach them with email &amp; SMS. All in one place.
              </p>
            </motion.div>

            <motion.div
              style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:20 }}
              variants={stagger} initial='hidden' whileInView='show' viewport={{ once:true }}
            >
              {FEATURES.map(f => (
                <motion.div
                  key={f.title}
                  variants={fadeUp}
                  whileHover={{ y:-5, boxShadow:'0 12px 32px rgba(0,0,0,0.1)' }}
                  style={{ background:'#ffffff', border:'1px solid #e5e7eb', borderRadius:14, padding:'24px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)', transition:'box-shadow 0.2s' }}
                >
                  <div style={{ width:44, height:44, borderRadius:12, background:f.bg, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
                    <i className={f.icon} style={{ fontSize:18, color:f.color }} />
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                    <h3 style={{ fontSize:15, fontWeight:800, color:'#111827', margin:0 }}>{f.title}</h3>
                    {f.badge && (
                      <span style={{
                        fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.04em',
                        padding:'2px 7px', borderRadius:99,
                        background: f.badge==='New' ? '#dcfce7' : '#fef3c7',
                        color:      f.badge==='New' ? '#166534' : '#92400e',
                      }}>{f.badge}</span>
                    )}
                  </div>
                  <p style={{ fontSize:14, color:'#6b7280', lineHeight:1.6, margin:0 }}>{f.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            HOW IT WORKS
        ═══════════════════════════════════════ */}
        <section style={{ background:'#ffffff', padding:'80px 0' }}>
          <div style={{ maxWidth:1140, margin:'0 auto', padding:'0 24px' }}>
            <motion.div style={{ textAlign:'center', marginBottom:52 }} variants={fadeUp} initial='hidden' whileInView='show' viewport={{ once:true }}>
              <h2 style={{ fontSize:'clamp(1.6rem, 4vw, 2.3rem)', fontWeight:900, color:'#111827', marginBottom:12 }}>
                Up and running in 3 steps
              </h2>
              <p style={{ fontSize:17, color:'#6b7280' }}>No learning curve. No agency needed. Just results.</p>
            </motion.div>

            <motion.div
              style={{ display:'flex', flexWrap:'wrap', gap:32 }}
              variants={stagger} initial='hidden' whileInView='show' viewport={{ once:true }}
            >
              {STEPS.map((s, i) => (
                <motion.div key={s.badge} variants={fadeUp} style={{ flex:'1 1 260px', position:'relative' }}>
                  {/* Connector line */}
                  {i < STEPS.length - 1 && (
                    <div style={{ position:'absolute', top:32, left:'calc(100% + 16px)', width:'calc(100% - 32px)', height:2, background:'linear-gradient(90deg, #e5e7eb, transparent)', display:'none' }} className='d-none d-md-block' />
                  )}
                  <div style={{ marginBottom:16, position:'relative' }}>
                    <img src={s.img} alt={s.title} style={{ width:'100%', height:200, objectFit:'cover', borderRadius:12 }} />
                    <div style={{ position:'absolute', top:14, left:14, width:36, height:36, borderRadius:'50%', background:'#0f172a', color:'#fff', fontSize:13, fontWeight:900, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.3)' }}>
                      {s.badge}
                    </div>
                  </div>
                  <h3 style={{ fontSize:16, fontWeight:800, color:'#111827', marginBottom:8 }}>{s.title}</h3>
                  <p style={{ fontSize:14, color:'#6b7280', lineHeight:1.6, margin:0 }}>{s.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            MOBILE APP SECTION
        ═══════════════════════════════════════ */}
        <section style={{ background:'linear-gradient(135deg, #0f172a, #1e3a5f)', padding:'80px 0', overflow:'hidden', position:'relative' }}>
          <div style={{ position:'absolute', top:-100, right:-100, width:400, height:400, borderRadius:'50%', background:'rgba(37,99,235,0.15)', filter:'blur(60px)' }} />
          <div style={{ maxWidth:1140, margin:'0 auto', padding:'0 24px' }}>
            <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:48 }}>
              <motion.div style={{ flex:'1 1 300px', display:'flex', justifyContent:'center' }} initial={{ y:30, opacity:0 }} whileInView={{ y:0, opacity:1 }} transition={{ duration:0.8 }} viewport={{ once:true }}>
                <img src={appMockup1} alt='Greatly Social mobile app' style={{ maxWidth:300, width:'100%' }} />
              </motion.div>
              <motion.div style={{ flex:'1 1 400px' }} variants={fadeUp} initial='hidden' whileInView='show' viewport={{ once:true }}>
                <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:99, padding:'5px 14px', marginBottom:20 }}>
                  <i className='fas fa-mobile-alt' style={{ fontSize:11, color:'#60a5fa' }} />
                  <span style={{ fontSize:12, fontWeight:700, color:'rgba(255,255,255,0.7)', textTransform:'uppercase', letterSpacing:'0.07em' }}>Now on iOS & Android</span>
                </div>
                <h2 style={{ fontSize:'clamp(1.6rem, 4vw, 2.4rem)', fontWeight:900, color:'#ffffff', lineHeight:1.2, marginBottom:16 }}>
                  <TextLoop interval={3000} mask springConfig={{ stiffness:180, damping:16 }}>
                    <span>Manage content on the go.</span>
                    <span>Draft posts from anywhere.</span>
                    <span>Your calendar in your pocket.</span>
                    <span>Never miss a moment.</span>
                  </TextLoop>
                </h2>
                <p style={{ fontSize:16, color:'rgba(255,255,255,0.65)', lineHeight:1.7, marginBottom:28 }}>
                  Draft posts, reschedule content, and manage your entire calendar from your phone. Your next great idea doesn't have to wait until you're at your desk.
                </p>
                <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
                  <a href='https://apps.apple.com/us/app/greatly-social/id1563430092' target='_blank' rel='noopener noreferrer'>
                    <img src={appStoreBadge} alt='Download on the App Store' style={{ height:46 }} />
                  </a>
                  <a href='https://play.google.com/store/apps/details?id=com.app.greatlysocial' target='_blank' rel='noopener noreferrer'>
                    <img src={googlePlayBadge} alt='Get it on Google Play' style={{ height:46 }} />
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            PRICING TEASER
        ═══════════════════════════════════════ */}
        <section style={{ background:'#f8fafc', padding:'80px 0' }}>
          <div style={{ maxWidth:1140, margin:'0 auto', padding:'0 24px' }}>
            <motion.div style={{ textAlign:'center', marginBottom:48 }} variants={fadeUp} initial='hidden' whileInView='show' viewport={{ once:true }}>
              <h2 style={{ fontSize:'clamp(1.6rem, 4vw, 2.3rem)', fontWeight:900, color:'#111827', marginBottom:12 }}>
                Simple, transparent pricing
              </h2>
              <p style={{ fontSize:17, color:'#6b7280' }}>Start free. Upgrade when you're ready. No hidden fees.</p>
            </motion.div>

            <motion.div
              style={{ display:'flex', flexWrap:'wrap', gap:20, justifyContent:'center' }}
              variants={stagger} initial='hidden' whileInView='show' viewport={{ once:true }}
            >
              {[
                { name:'Starter', price:'$9',  period:'/mo', desc:'Perfect for individuals getting started with social media.', features:['15 posts / month','5 AI credits / month','3 connected accounts','AI Post Creator','AI Caption Generator','Video Editor','File Manager (5 GB)','Content Calendar','Link in Bio','Built-in CRM'], cta:'Start Free Trial', highlight:false },
                { name:'Pro',     price:'$29', period:'/mo', desc:'For creators and small businesses ready to grow.', features:['30 posts / month','20 AI credits / month','10 connected accounts','AI Post Creator','AI Caption Generator','Video Editor','File Manager (25 GB)','Content Calendar','Link in Bio','Built-in CRM','Analytics & Insights'], cta:'Start Free Trial', highlight:true },
                { name:'Agency',  price:'$69', period:'/mo', desc:'Built for teams managing multiple clients at scale.', features:['100 posts / month','30 AI credits / month','20 connected accounts','AI Post Creator','AI Caption Generator','Video Editor','File Manager (100 GB)','Content Calendar','Link in Bio','Built-in CRM','Analytics & Insights'], cta:'Start Free Trial', highlight:false },
              ].map(plan => (
                <motion.div
                  key={plan.name}
                  variants={fadeUp}
                  style={{
                    flex        : '1 1 260px',
                    maxWidth    : 320,
                    background  : '#ffffff',
                    border      : `2px solid ${plan.highlight ? '#2563eb' : '#e5e7eb'}`,
                    borderRadius: 16,
                    padding     : '28px 24px',
                    position    : 'relative',
                    boxShadow   : plan.highlight ? '0 8px 32px rgba(37,99,235,0.15)' : '0 1px 4px rgba(0,0,0,0.05)',
                  }}
                >
                  {plan.highlight && (
                    <div style={{ position:'absolute', top:-13, left:'50%', transform:'translateX(-50%)', background:'#2563eb', color:'#fff', fontSize:11, fontWeight:700, padding:'3px 14px', borderRadius:99, whiteSpace:'nowrap' }}>
                      Most Popular
                    </div>
                  )}
                  <div style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.09em', color:'#9ca3af', marginBottom:8 }}>{plan.name}</div>
                  <div style={{ display:'flex', alignItems:'flex-end', gap:2, marginBottom:6 }}>
                    <span style={{ fontSize:40, fontWeight:900, color:'#111827', lineHeight:1 }}>{plan.price}</span>
                    <span style={{ fontSize:14, color:'#9ca3af', marginBottom:4 }}>{plan.period}</span>
                  </div>
                  <p style={{ fontSize:13, color:'#6b7280', marginBottom:20, lineHeight:1.5 }}>{plan.desc}</p>
                  <div style={{ marginBottom:24 }}>
                    {plan.features.map(f => (
                      <div key={f} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, fontSize:13, color:'#374151' }}>
                        <i className='fas fa-check' style={{ fontSize:10, color:'#16a34a' }} />
                        {f}
                      </div>
                    ))}
                  </div>
                  <a href='/register' style={{
                    ...btnPrimary,
                    display        : 'flex',
                    justifyContent : 'center',
                    background     : plan.highlight ? '#2563eb' : 'transparent',
                    border         : plan.highlight ? 'none' : '1.5px solid #2563eb',
                    color          : plan.highlight ? '#fff' : '#2563eb',
                    boxShadow      : plan.highlight ? '0 4px 14px rgba(37,99,235,0.3)' : 'none',
                    padding        : '10px 0',
                    fontSize       : 14,
                  }}>
                    {plan.cta}
                  </a>
                </motion.div>
              ))}
            </motion.div>

            <p style={{ textAlign:'center', fontSize:13, color:'#9ca3af', marginTop:20 }}>
              All plans include a 14-day free trial. <a href='/plans' style={{ color:'#2563eb', fontWeight:600 }}>See full plan details →</a>
            </p>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            TESTIMONIAL
        ═══════════════════════════════════════ */}
        <section style={{ background:'#ffffff', padding:'72px 0' }}>
          <div style={{ maxWidth:800, margin:'0 auto', padding:'0 24px', textAlign:'center' }}>
            <motion.div variants={fadeUp} initial='hidden' whileInView='show' viewport={{ once:true }}>
              <div style={{ display:'flex', justifyContent:'center', gap:4, marginBottom:20 }}>
                {[...Array(5)].map((_, i) => <StarFill key={i} size={18} color='#fbbf24' />)}
              </div>
              <img src={testimonyImg} alt='Jamie Rivera' style={{ width:72, height:72, borderRadius:'50%', objectFit:'cover', marginBottom:16, border:'3px solid #e5e7eb' }} />
              <blockquote style={{ fontSize:'clamp(1.1rem, 3vw, 1.4rem)', fontWeight:700, color:'#111827', lineHeight:1.5, margin:'0 0 16px' }}>
                "Greatly Social cut our scheduling time by 70% — and our engagement is up across every platform. It's the only tool we need."
              </blockquote>
              <figcaption style={{ fontSize:14, color:'#6b7280' }}>
                <strong style={{ color:'#374151' }}>Jamie Rivera</strong> — Marketing Manager, Fresh Fit Foods
              </figcaption>
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            FINAL CTA
        ═══════════════════════════════════════ */}
        <section style={{ background:'linear-gradient(135deg, #1d4ed8, #2563eb)', padding:'80px 24px', textAlign:'center' }}>
          <motion.div style={{ maxWidth:680, margin:'0 auto' }} variants={fadeUp} initial='hidden' whileInView='show' viewport={{ once:true }}>
            <h2 style={{ fontSize:'clamp(1.8rem, 4vw, 2.6rem)', fontWeight:900, color:'#ffffff', marginBottom:14 }}>
              Ready to save hours every week?
            </h2>
            <p style={{ fontSize:17, color:'rgba(255,255,255,0.75)', marginBottom:32, lineHeight:1.6 }}>
              Join thousands of small businesses, realtors, and agencies who use Greatly Social to stay consistent on social media — without the stress.
            </p>
            <div style={{ display:'flex', gap:14, justifyContent:'center', flexWrap:'wrap' }}>
              <a href='/register' style={{ ...btnPrimary, background:'#ffffff', color:'#1d4ed8', fontSize:16, padding:'14px 32px', boxShadow:'0 4px 20px rgba(0,0,0,0.2)' }}
                onMouseEnter={e => e.currentTarget.style.background='#f0f9ff'}
                onMouseLeave={e => e.currentTarget.style.background='#ffffff'}>
                Start for Free — 14 Days
              </a>
              <a href='/plans' style={{ ...btnOutline, border:'2px solid rgba(255,255,255,0.5)', color:'#fff', fontSize:16 }}
                onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                View Plans
              </a>
            </div>
            <div style={{ display:'flex', gap:24, justifyContent:'center', marginTop:24, flexWrap:'wrap' }}>
              {['No credit card required', 'Cancel anytime', '14-day free trial'].map(t => (
                <span key={t} style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'rgba(255,255,255,0.6)' }}>
                  <CheckCircleFill size={13} color='#86efac' />{t}
                </span>
              ))}
            </div>
          </motion.div>
        </section>

      </div>
    </>
  )
}